import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '../components/page-action-bar/page-action-bar';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio/radio.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import {
  DeliveryStatus,
  IDelivery,
  IDeliveryConfirmLineFailure,
  IDeliveryLineRequest,
  IPurchaseOrder,
  IStockItem,
  ISupplier,
} from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

const STATUS_VARIANT: Record<
  DeliveryStatus,
  'neutral' | 'primary' | 'warning' | 'success' | 'danger'
> = {
  pending: 'warning',
  confirmed: 'success',
  cancelled: 'neutral',
};

// Combined create + edit page. /deliveries/new for create, /deliveries/:id
// for edit. Two create modes:
//   ad-hoc — operator picks supplier and adds lines manually
//   po-linked — operator picks an open PO; server prefills the lines from
//               outstanding qty
// Line edits are allowed only while Pending. Confirm = the high-stakes
// step: server fires per-line StockMovement (delivery_received) +
// OnHand bump + cost moving-avg + (PO-linked) PO ratchet.
@customElement('delivery-edit-page')
export class DeliveryEditPage extends Base {
  @state() private _existing: IDelivery | null = null;
  @state() private _loading = true;
  @state() private _busy = false;
  @state() private _error: string | null = null;
  @state() private _confirmFailures: IDeliveryConfirmLineFailure[] = [];

  // Lookups for pickers.
  @state() private _suppliers: ISupplier[] = [];
  @state() private _stock: IStockItem[] = [];
  @state() private _openPos: IPurchaseOrder[] = [];

  // Form fields.
  @state() private _mode: 'adhoc' | 'po-linked' = 'adhoc';
  @state() private _supplierId = '';
  @state() private _purchaseOrderId = '';
  @state() private _deliveryNoteNumber = '';
  @state() private _note = '';
  @state() private _lines: IDeliveryLineRequest[] = [];

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 1000px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      @media (max-width: 720px) {
        :host {
          padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
        }
      }
      header.bar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
      }
      header.bar h2 {
        margin: 0;
        flex: 1;
      }
      form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      .line-row {
        display: grid;
        grid-template-columns: 1fr 90px 110px 110px 32px;
        gap: 8px;
        align-items: end;
      }
      .line-row.locked {
        grid-template-columns: 1fr 90px 110px 110px;
      }
      .line-row .small {
        font-size: 0.78rem;
        opacity: 0.7;
      }
      .right {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
      .small-add {
        margin-top: 4px;
      }
      footer {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        margin-top: 16px;
        flex-wrap: wrap;
      }
      .summary {
        font-variant-numeric: tabular-nums;
        opacity: 0.85;
      }
      .mode-pick {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
    `,
  ];

  firstUpdated() {
    void this._load();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop) void this._load();
  }

  private async _load() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    this._loading = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    const [supR, stockR, posR] = await Promise.all([
      api.listSuppliers(this.activeShop.shopName),
      api.listStock(this.activeShop.shopName),
      api.listPurchaseOrders(this.activeShop.shopName),
    ]);
    if (supR.data) this._suppliers = supR.data.filter((s) => s.isActive);
    if (stockR.data) this._stock = stockR.data.filter((s) => s.isActive);
    if (posR.data) {
      // Only Placed / PartiallyReceived POs are deliverable.
      this._openPos = posR.data.filter(
        (p) => p.status === 'placed' || p.status === 'partially_received'
      );
    }

    const editId = readEditIdFromPath();
    if (editId && editId !== 'new') {
      const r = await api.getDelivery(this.activeShop.shopName, editId);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Delivery not found.';
        this._loading = false;
        return;
      }
      const d = r.data;
      this._existing = d;
      this._supplierId = d.supplierId;
      this._purchaseOrderId = d.purchaseOrderId ?? '';
      this._deliveryNoteNumber = d.deliveryNoteNumber ?? '';
      this._note = d.note ?? '';
      this._mode = d.purchaseOrderId ? 'po-linked' : 'adhoc';
      this._lines = d.lines.map((l) => ({
        stockItemId: l.stockItemId,
        actualQuantityInPurchaseUnit: l.actualQuantityInPurchaseUnit,
        unitCostInPurchaseUnit: l.unitCostInPurchaseUnit,
        note: l.note ?? undefined,
      }));
    }
    this._loading = false;
  }

  private get _isEdit(): boolean {
    return this._existing !== null;
  }

  private get _isPendingEditable(): boolean {
    return !this._existing || this._existing.status === 'pending';
  }

  private get _subtotal(): number {
    return this._lines.reduce(
      (sum, l) =>
        sum +
        (l.actualQuantityInPurchaseUnit || 0) * (l.unitCostInPurchaseUnit || 0),
      0
    );
  }

  private _addLine() {
    this._lines = [
      ...this._lines,
      {
        stockItemId: '',
        actualQuantityInPurchaseUnit: 0,
        unitCostInPurchaseUnit: 0,
      },
    ];
  }
  private _removeLine(i: number) {
    this._lines = this._lines.filter((_, idx) => idx !== i);
  }
  private _updateLine(i: number, patch: Partial<IDeliveryLineRequest>) {
    this._lines = this._lines.map((l, idx) =>
      idx === i ? { ...l, ...patch } : l
    );
  }

  private _onPoChange(poId: string) {
    this._purchaseOrderId = poId;
    const po = this._openPos.find((p) => p.id === poId);
    if (po) this._supplierId = po.supplierId;
    this._error = null;
  }

  private async _create() {
    if (!this.activeShop || this._busy) return;
    if (this._mode === 'po-linked') {
      if (!this._purchaseOrderId) {
        this._error = 'Pick a purchase order.';
        return;
      }
      if (!this._supplierId) {
        this._error = 'Supplier is required.';
        return;
      }
    } else {
      if (!this._supplierId) {
        this._error = 'Supplier is required.';
        return;
      }
      if (this._lines.length === 0) {
        this._error = 'Add at least one line.';
        return;
      }
      for (const l of this._lines) {
        if (
          !l.stockItemId ||
          l.actualQuantityInPurchaseUnit <= 0 ||
          l.unitCostInPurchaseUnit < 0
        ) {
          this._error =
            'Each line needs a stock item, qty > 0, and non-negative unit cost.';
          return;
        }
      }
    }
    this._busy = true;
    this._error = null;
    const r = await ApiFactory.authenticated().createDelivery(
      this.activeShop.shopName,
      {
        supplierId: this._supplierId,
        purchaseOrderId:
          this._mode === 'po-linked' ? this._purchaseOrderId : undefined,
        deliveryNoteNumber: this._deliveryNoteNumber.trim() || undefined,
        note: this._note.trim() || undefined,
        lines: this._mode === 'adhoc' ? this._lines : undefined,
      }
    );
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Create failed.';
      return;
    }
    router.navigate(
      resolveRouterPath(`deliveries/${encodeURIComponent(r.data.id)}`)
    );
  }

  private async _save() {
    if (!this.activeShop || !this._existing || this._busy) return;
    for (const l of this._lines) {
      if (
        !l.stockItemId ||
        l.actualQuantityInPurchaseUnit <= 0 ||
        l.unitCostInPurchaseUnit < 0
      ) {
        this._error =
          'Each line needs a stock item, qty > 0, and non-negative unit cost.';
        return;
      }
    }
    this._busy = true;
    this._error = null;
    const r = await ApiFactory.authenticated().updateDelivery(
      this.activeShop.shopName,
      this._existing.id,
      {
        deliveryNoteNumber: this._deliveryNoteNumber.trim() || null,
        note: this._note.trim() || null,
        lines: this._lines,
      }
    );
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Save failed.';
      return;
    }
    this._existing = r.data;
  }

  private async _confirm() {
    if (!this.activeShop || !this._existing || this._busy) return;
    if (
      !confirm(
        'Confirm this delivery? OnHand will increase, the moving-average cost will update, and (if PO-linked) the PO ratchet will advance.'
      )
    )
      return;
    this._busy = true;
    this._confirmFailures = [];
    this._error = null;
    const r = await ApiFactory.authenticated().confirmDelivery(
      this.activeShop.shopName,
      this._existing.id
    );
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Confirm failed.';
      return;
    }
    this._existing = r.data.delivery;
    this._confirmFailures = r.data.lineFailures;
  }

  private async _cancel() {
    if (!this.activeShop || !this._existing || this._busy) return;
    if (!confirm('Cancel this delivery? No stock will change.')) return;
    this._busy = true;
    const r = await ApiFactory.authenticated().cancelDelivery(
      this.activeShop.shopName,
      this._existing.id
    );
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Cancel failed.';
      return;
    }
    this._existing = r.data;
  }

  private _back() {
    router.navigate(resolveRouterPath('deliveries'));
  }

  private _stockName(id: string): string {
    return this._stock.find((s) => s.id === id)?.name ?? id;
  }
  private _supplierName(id: string): string {
    return this._suppliers.find((s) => s.id === id)?.name ?? id;
  }

  private _renderModeSwitch() {
    if (this._isEdit) return nothing;
    return html`
      <div class="mode-pick">
        <strong>Mode</strong>
        <sl-radio-group
          .value=${this._mode}
          @sl-change=${(e: Event) =>
            (this._mode = (e.target as HTMLInputElement).value as
              'adhoc' | 'po-linked')}
        >
          <sl-radio value="adhoc"
            >Ad-hoc — supplier showed up unannounced</sl-radio
          >
          <sl-radio value="po-linked"
            >PO-linked — receiving against an open purchase order</sl-radio
          >
        </sl-radio-group>
      </div>
    `;
  }

  private _renderLines() {
    if (this._isEdit && this._existing) {
      // Show prefilled / persisted lines. Editable while pending; locked
      // once confirmed / cancelled.
      if (!this._isPendingEditable) {
        return html`
          <h3>Lines</h3>
          ${this._existing.lines.map(
            (l) => html`
              <div class="line-row locked">
                <div>
                  <strong>${this._stockName(l.stockItemId)}</strong>
                  ${
                    l.purchaseOrderLineId
                      ? html`<div class="small">PO line</div>`
                      : nothing
                  }
                </div>
                <div class="right">
                  ${
                    l.expectedQuantityInPurchaseUnit !== null
                      ? html`<small class="small"
                          >expected ${l.expectedQuantityInPurchaseUnit}</small
                        >`
                      : nothing
                  }
                  <div>${l.actualQuantityInPurchaseUnit}</div>
                </div>
                <div class="right">${l.unitCostInPurchaseUnit.toFixed(2)}</div>
                <div class="right">
                  ${(l.actualQuantityInPurchaseUnit * l.unitCostInPurchaseUnit).toFixed(2)}
                </div>
              </div>
            `
          )}
        `;
      }
      // Pending — editable.
      return html`
        <h3>Lines</h3>
        ${this._lines.map(
          (line, i) => html`
            <div class="line-row">
              <sl-select
                .value=${line.stockItemId}
                placeholder="Pick a stock item"
                @sl-change=${(e: Event) =>
                  this._updateLine(i, {
                    stockItemId: (e.target as HTMLSelectElement).value,
                  })}
              >
                ${this._stock.map(
                  (s) =>
                    html`<sl-option value=${s.id}
                      >${s.name} (${s.baseUnit})</sl-option
                    >`
                )}
              </sl-select>
              <sl-input
                type="number"
                min="0"
                step="any"
                label="Actual"
                .value=${String(line.actualQuantityInPurchaseUnit)}
                @sl-input=${(e: Event) =>
                  this._updateLine(i, {
                    actualQuantityInPurchaseUnit:
                      parseFloat((e.target as HTMLInputElement).value) || 0,
                  })}
              ></sl-input>
              <sl-input
                type="number"
                min="0"
                step="any"
                label="Unit cost"
                .value=${String(line.unitCostInPurchaseUnit)}
                @sl-input=${(e: Event) =>
                  this._updateLine(i, {
                    unitCostInPurchaseUnit:
                      parseFloat((e.target as HTMLInputElement).value) || 0,
                  })}
              ></sl-input>
              <sl-icon-button
                name="trash"
                label="Remove"
                @click=${() => this._removeLine(i)}
              ></sl-icon-button>
            </div>
          `
        )}
        <sl-button size="small" class="small-add" @click=${this._addLine}
          >Add line</sl-button
        >
      `;
    }
    // Create flow — only the ad-hoc path needs line entry on this page;
    // PO-linked deliveries get prefilled lines on create and the operator
    // adjusts after navigating to the new delivery's edit page.
    if (this._mode === 'po-linked') return nothing;
    return html`
      <h3>Lines</h3>
      ${
        this._lines.length === 0
          ? html`<p class="small-add">No lines yet — add at least one.</p>`
          : nothing
      }
      ${this._lines.map(
        (line, i) => html`
          <div class="line-row">
            <sl-select
              .value=${line.stockItemId}
              placeholder="Pick a stock item"
              @sl-change=${(e: Event) =>
                this._updateLine(i, {
                  stockItemId: (e.target as HTMLSelectElement).value,
                })}
            >
              ${this._stock.map(
                (s) =>
                  html`<sl-option value=${s.id}
                    >${s.name} (${s.baseUnit})</sl-option
                  >`
              )}
            </sl-select>
            <sl-input
              type="number"
              min="0"
              step="any"
              label="Actual"
              .value=${String(line.actualQuantityInPurchaseUnit)}
              @sl-input=${(e: Event) =>
                this._updateLine(i, {
                  actualQuantityInPurchaseUnit:
                    parseFloat((e.target as HTMLInputElement).value) || 0,
                })}
            ></sl-input>
            <sl-input
              type="number"
              min="0"
              step="any"
              label="Unit cost"
              .value=${String(line.unitCostInPurchaseUnit)}
              @sl-input=${(e: Event) =>
                this._updateLine(i, {
                  unitCostInPurchaseUnit:
                    parseFloat((e.target as HTMLInputElement).value) || 0,
                })}
            ></sl-input>
            <sl-icon-button
              name="trash"
              label="Remove"
              @click=${() => this._removeLine(i)}
            ></sl-icon-button>
          </div>
        `
      )}
      <sl-button size="small" class="small-add" @click=${this._addLine}
        >Add line</sl-button
      >
    `;
  }

  render() {
    if (this._loading) return html`<sl-card><p>Loading…</p></sl-card>`;
    const subtotal = this._subtotal;
    const status: DeliveryStatus = this._existing?.status ?? 'pending';

    return html`
      <header class="bar">
        <sl-icon-button
          name="arrow-left"
          label="Back"
          @click=${this._back}
        ></sl-icon-button>
        <h2>${this._isEdit ? 'Delivery' : 'New delivery'}</h2>
        ${
          this._existing
            ? html`<sl-badge variant=${STATUS_VARIANT[status]} pill
                >${status}</sl-badge
              >`
            : nothing
        }
      </header>

      ${this._error ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>` : nothing}
      ${
        this._confirmFailures.length > 0
          ? html`
              <sl-alert variant="warning" open>
                <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
                <strong
                  >${this._confirmFailures.length} line
                  failure${this._confirmFailures.length === 1 ? '' : 's'}</strong
                >
                <ul>
                  ${this._confirmFailures.map(
                    (f) => html`
                      <li>
                        ${f.stockItemId ? this._stockName(f.stockItemId) : f.lineId}:
                        ${f.reason}
                      </li>
                    `
                  )}
                </ul>
              </sl-alert>
            `
          : nothing
      }

      <sl-card>
        <form
          @submit=${(e: Event) => {
            e.preventDefault();
            void (this._isEdit ? this._save() : this._create());
          }}
        >
          ${this._renderModeSwitch()}
          ${
            !this._isEdit && this._mode === 'po-linked'
              ? html`
                  <sl-select
                    label="Purchase order"
                    help-text="Only Placed / Partially-received POs are deliverable."
                    .value=${this._purchaseOrderId}
                    @sl-change=${(e: Event) => this._onPoChange((e.target as HTMLSelectElement).value)}
                    required
                  >
                    ${this._openPos.map(
                      (p) => html`
                        <sl-option value=${p.id}>
                          ${this._supplierName(p.supplierId)} —
                          ${p.lines.length} lines · subtotal
                          ${p.subtotal.toFixed(2)} ·
                          ${p.status.replace('_', ' ')}
                        </sl-option>
                      `
                    )}
                  </sl-select>
                `
              : html`
                  <sl-select
                    label="Supplier"
                    .value=${this._supplierId}
                    ?disabled=${this._isEdit}
                    @sl-change=${(e: Event) =>
                      (this._supplierId = (
                        e.target as HTMLSelectElement
                      ).value)}
                    required
                  >
                    ${this._suppliers.map(
                      (s) =>
                        html`<sl-option value=${s.id}>${s.name}</sl-option>`
                    )}
                  </sl-select>
                `
          }

          <div class="row">
            <sl-input
              label="Delivery note number"
              help-text="Supplier's invoice / DN, optional."
              .value=${this._deliveryNoteNumber}
              ?disabled=${!this._isPendingEditable}
              @sl-input=${(e: Event) =>
                (this._deliveryNoteNumber = (
                  e.target as HTMLInputElement
                ).value)}
            ></sl-input>
            <sl-textarea
              label="Note"
              rows="2"
              .value=${this._note}
              ?disabled=${!this._isPendingEditable}
              @sl-input=${(e: Event) => (this._note = (e.target as HTMLInputElement).value)}
            ></sl-textarea>
          </div>

          <sl-divider></sl-divider>
          ${this._renderLines()}

          <div class="summary right">
            Subtotal: <strong>${subtotal.toFixed(2)}</strong>
          </div>
        </form>
      </sl-card>

      <page-action-bar>
        ${
          !this._isEdit
            ? html`
                <sl-button
                  slot="right"
                  variant="primary"
                  ?loading=${this._busy}
                  @click=${this._create}
                >
                  Create delivery
                </sl-button>
              `
            : nothing
        }
        ${
          this._isEdit && this._isPendingEditable
            ? html`
                <sl-button
                  slot="left"
                  variant="warning"
                  ?disabled=${this._busy}
                  @click=${this._cancel}
                >
                  Cancel
                </sl-button>
                <sl-button
                  slot="right"
                  variant="default"
                  ?loading=${this._busy}
                  @click=${this._save}
                >
                  Save lines
                </sl-button>
                <sl-button
                  slot="right"
                  variant="primary"
                  ?loading=${this._busy}
                  @click=${this._confirm}
                >
                  Confirm
                </sl-button>
              `
            : nothing
        }
      </page-action-bar>
    `;
  }
}

function readEditIdFromPath(): string | null {
  const m = window.location.pathname.match(/\/deliveries\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}
